
import React from 'react';
import { Lead, LeadContact, leadPriorityLabels, leadStatusLabels, LeadPriority, LeadStatus } from '@/components/leads/types';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { useUpdateLeadFields } from '@/hooks/useLeads';

// Import new section components
import { CompanyInformationSection } from './sections/CompanyInformationSection';
import { OpportunityDetailsSection } from './sections/OpportunityDetailsSection';
import { BusinessDevelopmentSection } from './sections/BusinessDevelopmentSection';
import { PrimaryContactSection } from './sections/PrimaryContactSection';
import { NotesSection } from './sections/NotesSection';

interface LeadInfoProps {
  lead: Lead;
  // onUpdate prop removed as query invalidation handles updates
}

export const LeadInfo: React.FC<LeadInfoProps> = ({ lead }) => {
  // Correctly use isPending for mutation loading state from @tanstack/react-query v5
  const { mutateAsync: updateLeadFields, isPending: isUpdatingLead } = useUpdateLeadFields();

  const handleFieldSave = async (leadId: string, fieldName: keyof Lead, value: any) => {
    await updateLeadFields({ leadId, fieldsToUpdate: { [fieldName]: value } });
  };

  const getStatusColor = (status: keyof typeof leadStatusLabels | string) => {
    switch (status) {
      case "new": return "bg-blue-100 text-blue-800";
      case "contacted": return "bg-sky-100 text-sky-800";
      case "meeting": return "bg-indigo-100 text-indigo-800";
      case "proposal": return "bg-amber-100 text-amber-800";
      case "negotiating": return "bg-orange-100 text-orange-800";
      case "won": return "bg-green-100 text-green-800";
      case "lost": return "bg-red-100 text-red-800";
      case "onhold": return "bg-slate-100 text-slate-800";
      default: return "bg-gray-100 text-gray-800";
    }
  };

  const getPriorityColor = (priority: keyof typeof leadPriorityLabels | string) => {
    switch (priority) {
      case "high": return "bg-red-100 text-red-800";
      case "medium": return "bg-yellow-100 text-yellow-800";
      case "low": return "bg-green-100 text-green-800";
      default: return "bg-gray-100 text-gray-800";
    }
  };

  const formatBadgeDisplay = (value: any, fieldName: keyof Lead, selectOptions?: Record<string, string>) => {
    if (fieldName === 'priority' && value in leadPriorityLabels) {
      return <Badge className={getPriorityColor(value as LeadPriority)}>{leadPriorityLabels[value as LeadPriority]}</Badge>;
    }
    if (fieldName === 'status' && value in leadStatusLabels) {
      return <Badge className={getStatusColor(value as LeadStatus)}>{leadStatusLabels[value as LeadStatus]}</Badge>;
    }
    // For other fields or if no specific badge, return the value as is (EditableDetailItem handles N/A for empty strings)
    return value;
  };
  
  const primaryContact: LeadContact | undefined = lead.contacts && lead.contacts.length > 0 ? lead.contacts[0] : undefined;

  return (
    <Card>
      <CardHeader>
        <CardTitle>Company Profile</CardTitle>
        <CardDescription>Key details about the company and associated opportunity. Fields with an edit icon can be updated inline.</CardDescription>
      </CardHeader>
      <CardContent className="divide-y divide-gray-200">
        <CompanyInformationSection 
          lead={lead}
          handleFieldSave={handleFieldSave}
          isUpdatingLead={isUpdatingLead}
        />
        <OpportunityDetailsSection
          lead={lead}
          handleFieldSave={handleFieldSave}
          isUpdatingLead={isUpdatingLead}
        />
        <BusinessDevelopmentSection
          lead={lead}
          handleFieldSave={handleFieldSave}
          isUpdatingLead={isUpdatingLead}
          formatBadgeDisplay={formatBadgeDisplay}
        />
        <PrimaryContactSection 
          primaryContact={primaryContact}
        />
        <NotesSection
          lead={lead}
          handleFieldSave={handleFieldSave}
          isUpdatingLead={isUpdatingLead}
        />
      </CardContent>
    </Card>
  );
};
